import { useEffect, useState } from "react";

import { faCheck, faThumbtack } from "@fortawesome/pro-regular-svg-icons";

import type { MetricsQueryRow } from "src/analytics/metrics/useMetricsQuery";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { Button } from "src/design-system/Button";
import { Modal } from "src/design-system/Modal";

/** Data ChatChartCard sends when the user clicks Pin. */
export type PinPreviewData = {
  defaultTitle: string;
  chartDefinition: Record<string, unknown>;
  rows: MetricsQueryRow[];
  labelDictionaries?: LabelDictionaries;
};

/** Full data the parent modal needs (preview + pin identity). */
export type PinModalData = PinPreviewData & {
  sql: string;
  pinId: string;
  /** SQL placeholders the chart binds; persisted so dashboard filters bind at render. */
  parameterNames: string[];
};

const CREATE_DESCRIPTION =
  "Pinning this chart to the dashboard makes it available across your organization. Your copilot chat stays private.";

const COPY = {
  create: {
    header: "Pin chart to the dashboard",
    description: CREATE_DESCRIPTION,
    buttonIcon: faThumbtack,
    buttonLabel: "Pin",
  },
  edit: {
    header: "Rename chart",
    description: undefined,
    buttonIcon: faCheck,
    buttonLabel: "Save",
  },
} as const;

export const PinChartModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: (title: string) => void;
  defaultTitle: string;
  afterLeave?: () => void;
  mode?: "create" | "edit";
  children: React.ReactNode;
}> = ({
  open,
  onClose,
  onConfirm,
  defaultTitle,
  afterLeave,
  mode = "create",
  children,
}) => {
  const [title, setTitle] = useState(defaultTitle);
  const copy = COPY[mode];

  // Reset title when the modal opens with a new defaultTitle.
  useEffect(() => {
    if (open) setTitle(defaultTitle);
  }, [open, defaultTitle]);

  return (
    <Modal afterLeave={afterLeave} open={open} size="md" onClose={onClose}>
      <Modal.Header description={copy.description}>{copy.header}</Modal.Header>
      <div className="border-gray-200 border-t border-b px-5 py-5 pb-6">
        <div className="overflow-hidden rounded-md border border-gray-200">
          <input
            autoFocus
            className="w-full px-4 py-3 font-inter-semibold-13px text-gray-800 outline-none placeholder:text-gray-400"
            data-loc="pin-chart-title-input"
            placeholder="Chart title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="h-64 p-4">{children}</div>
        </div>
      </div>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="pin-chart-confirm"
            isDisabled={
              title.trim().length === 0 ||
              (mode === "edit" && title.trim() === defaultTitle.trim())
            }
            iconLeft={copy.buttonIcon}
            variant="primary"
            onClick={() => onConfirm(title.trim())}
          >
            {copy.buttonLabel}
          </Button>
        }
        secondaryButton="Cancel"
      />
    </Modal>
  );
};
